import React from 'react';
import { OI_SLIDER_MAX } from '../../utils/liquidityRule';
import { AdvancedFilters, COVERED_CALL_DEFAULT_FILTERS } from './filters';
import { getMarketCapLabel, getSectorLabel } from './filters/SectorMapping';

interface ActiveFilterPillsProps {
  filters: AdvancedFilters;
  onRemoveFilter: (key: keyof AdvancedFilters, resetValue?: any) => void;
}

// Helper to check if a value differs from default
const differsFromDefault = (
  value: any,
  defaultValue: any,
  key: string
): boolean => {
  // Handle array comparison
  if (Array.isArray(value) && Array.isArray(defaultValue)) {
    if (value.length !== defaultValue.length) return true;
    return !value.every(v => defaultValue.includes(v));
  }

  // For range filters, check against full range
  if (key.endsWith('_min') || key.endsWith('_max')) {
    if (value === undefined || value === null) return false;
    if (defaultValue === undefined) {
      // No default set - check if value is at boundary
      if (key.endsWith('_min')) return value > 0;
      if (key.endsWith('_max')) return value < 100;
    }
    return value !== defaultValue;
  }

  // Handle undefined/null
  if (value === undefined || value === null) return false;
  if (defaultValue === undefined) return true;

  return value !== defaultValue;
};

const ActiveFilterPills: React.FC<ActiveFilterPillsProps> = ({
  filters,
  onRemoveFilter
}) => {
  const pills: React.ReactNode[] = [];
  const defaults = COVERED_CALL_DEFAULT_FILTERS;

  // IV Rank
  const hasIVRankMin = filters.iv_rank_min !== undefined && filters.iv_rank_min > 0;
  const hasIVRankMax = filters.iv_rank_max !== undefined && filters.iv_rank_max < 100;
  if (hasIVRankMin || hasIVRankMax) {
    const minVal = filters.iv_rank_min ?? 0;
    const maxVal = filters.iv_rank_max ?? 100;
    let label = '';
    if (hasIVRankMin && hasIVRankMax) {
      label = `IV rank ${minVal}–${maxVal}`;
    } else if (hasIVRankMin) {
      label = `IV rank ≥ ${minVal}`;
    } else {
      label = `IV rank ≤ ${maxVal}`;
    }
    pills.push(
      <FilterPill
        key="iv_rank"
        label={label}
        onRemove={() => onRemoveFilter('iv_rank_min')}
      />
    );
  }

  // RSI
  const hasRSIMin = filters.rsi_min !== undefined && filters.rsi_min > 0;
  const hasRSIMax = filters.rsi_max !== undefined && filters.rsi_max < 100;
  if (hasRSIMin || hasRSIMax) {
    const minVal = filters.rsi_min ?? 0;
    const maxVal = filters.rsi_max ?? 100;
    let label = '';
    if (hasRSIMin && hasRSIMax) {
      label = `RSI ${minVal}–${maxVal}`;
    } else if (hasRSIMin) {
      label = `RSI ≥ ${minVal}`;
    } else {
      label = `RSI ≤ ${maxVal}`;
    }
    pills.push(
      <FilterPill
        key="rsi"
        label={label}
        onRemove={() => onRemoveFilter('rsi_min')}
      />
    );
  }

  // Spread
  const hasSpreadMax = filters.spread_pct_max !== undefined && filters.spread_pct_max < 30;
  if (hasSpreadMax) {
    pills.push(
      <FilterPill
        key="spread"
        label={`Spread ≤ ${filters.spread_pct_max}%`}
        onRemove={() => onRemoveFilter('spread_pct_max')}
      />
    );
  }

  // Market Cap Categories
  if (filters.market_cap_categories && filters.market_cap_categories.length > 0) {
    const labels = filters.market_cap_categories.map(c => getMarketCapLabel(c));
    const displayLabel = labels.length <= 2
      ? labels.join(', ')
      : `${labels.length} caps`;
    pills.push(
      <FilterPill
        key="market_cap"
        label={`Market cap: ${displayLabel}`}
        onRemove={() => onRemoveFilter('market_cap_categories')}
      />
    );
  }

  // Earnings
  if (filters.exclude_earnings_within_days !== undefined && filters.exclude_earnings_within_days > 0) {
    pills.push(
      <FilterPill
        key="exclude_earnings"
        label={`No earnings in ${filters.exclude_earnings_within_days} days`}
        onRemove={() => onRemoveFilter('exclude_earnings_within_days')}
      />
    );
  }
  if (filters.earnings_within_days !== undefined && filters.earnings_within_days > 0) {
    pills.push(
      <FilterPill
        key="earnings_within"
        label={`Earnings in ${filters.earnings_within_days} days`}
        onRemove={() => onRemoveFilter('earnings_within_days')}
      />
    );
  }

  // Annualized Yield
  const hasYieldMin = filters.annualized_yield_min !== undefined && filters.annualized_yield_min > 0;
  const hasYieldMax = filters.annualized_yield_max !== undefined && filters.annualized_yield_max < 100;
  if (hasYieldMin || hasYieldMax) {
    const minVal = filters.annualized_yield_min ?? 0;
    const maxVal = filters.annualized_yield_max ?? 100;
    let label = '';
    if (hasYieldMin && hasYieldMax) {
      label = `Premium yield ${minVal}–${maxVal}%`;
    } else if (hasYieldMin) {
      label = `Premium yield ≥ ${minVal}%`;
    } else {
      label = `Premium yield ≤ ${maxVal}%`;
    }
    pills.push(
      <FilterPill
        key="annualized_yield"
        label={label}
        onRemove={() => onRemoveFilter('annualized_yield_min')}
      />
    );
  }

  // Dividend Yield
  const hasDivMin = filters.dividend_yield_min !== undefined && filters.dividend_yield_min > 0;
  const hasDivMax = filters.dividend_yield_max !== undefined && filters.dividend_yield_max < 15;
  if (hasDivMin || hasDivMax) {
    const minVal = filters.dividend_yield_min ?? 0;
    const maxVal = filters.dividend_yield_max ?? 15;
    let label = '';
    if (hasDivMin && hasDivMax) {
      label = `Dividend ${minVal}–${maxVal}%`;
    } else if (hasDivMin) {
      label = `Dividend ≥ ${minVal}%`;
    } else {
      label = `Dividend ≤ ${maxVal}%`;
    }
    pills.push(
      <FilterPill
        key="dividend_yield"
        label={label}
        onRemove={() => onRemoveFilter('dividend_yield_min')}
      />
    );
  }

  // Net Margin
  const hasMarginMin = filters.net_margin_min !== undefined && filters.net_margin_min > -50;
  const hasMarginMax = filters.net_margin_max !== undefined && filters.net_margin_max < 50;
  if (hasMarginMin || hasMarginMax) {
    const minVal = filters.net_margin_min ?? -50;
    const maxVal = filters.net_margin_max ?? 50;
    let label = '';
    if (hasMarginMin && hasMarginMax) {
      label = `Net margin ${minVal}–${maxVal}%`;
    } else if (hasMarginMin) {
      label = `Net margin ≥ ${minVal}%`;
    } else {
      label = `Net margin ≤ ${maxVal}%`;
    }
    pills.push(
      <FilterPill
        key="net_margin"
        label={label}
        onRemove={() => onRemoveFilter('net_margin_min')}
      />
    );
  }

  // Debt/Equity
  if (filters.debt_equity_max !== undefined && filters.debt_equity_max < 5) {
    pills.push(
      <FilterPill
        key="debt_equity"
        label={`Debt/equity ≤ ${filters.debt_equity_max}×`}
        onRemove={() => onRemoveFilter('debt_equity_max')}
      />
    );
  }

  // Sectors
  if (filters.sectors && filters.sectors.length > 0) {
    const labels = filters.sectors.map(s => getSectorLabel(s));
    const displayLabel = labels.length <= 2
      ? labels.join(', ')
      : `${labels.length} sectors`;
    pills.push(
      <FilterPill
        key="sectors"
        label={`Sectors: ${displayLabel}`}
        onRemove={() => onRemoveFilter('sectors')}
      />
    );
  }

  // Trend Signals
  if (filters.trend_signals && filters.trend_signals.length > 0) {
    const displayLabel = filters.trend_signals.length === 1
      ? filters.trend_signals[0]
      : `${filters.trend_signals.length} trends`;
    pills.push(
      <FilterPill
        key="trend_signals"
        label={`Moving avg: ${displayLabel}`}
        onRemove={() => onRemoveFilter('trend_signals')}
      />
    );
  }

  // Income fit (Dimension 1)
  if (filters.min_dimension1_stars !== undefined) {
    pills.push(
      <FilterPill
        key="dimension1_stars"
        label={`Income fit ≥ ${(filters.min_dimension1_stars ?? 0) * 10} / 50`}
        onRemove={() => onRemoveFilter('min_dimension1_stars')}
      />
    );
  }

  // Timing (Dimension 2 Stars)
  if (filters.min_dimension2_stars !== undefined) {
    pills.push(
      <FilterPill
        key="dimension2_stars"
        label={`Timing ≥ ${(filters.min_dimension2_stars ?? 0) * 10} / 50`}
        onRemove={() => onRemoveFilter('min_dimension2_stars')}
      />
    );
  }

  // Avg 35-day call/put (baseline P&L)
  if ((filters.baseline_pnl_min ?? -3) > -3 || (filters.baseline_pnl_max ?? 4) < 4) {
    const lo = filters.baseline_pnl_min ?? -3;
    const hi = filters.baseline_pnl_max ?? 4;
    const sgn = (v: number) => `${v > 0 ? '+' : ''}${v}%`;
    pills.push(
      <FilterPill
        key="baseline_pnl"
        label={lo > -3 && hi < 4 ? `Net result ${sgn(lo)} to ${sgn(hi)}` : lo > -3 ? `Net result ≥ ${sgn(lo)}` : `Net result ≤ ${sgn(hi)}`}
        onRemove={() => onRemoveFilter('baseline_pnl_min')}
      />
    );
  }

  // This setup vs usual
  if (filters.setup_signals && filters.setup_signals.length > 0) {
    const SETUP_LABELS: Record<string, string> = {
      good_setup: 'stock & peers above usual',
      acceptable: 'peers above usual',
      tactical: 'stock above usual',
      unfavorable: 'below usual',
    };
    const text = filters.setup_signals.length === 1
      ? SETUP_LABELS[filters.setup_signals[0]] ?? filters.setup_signals[0]
      : `${filters.setup_signals.length} choices`;
    pills.push(
      <FilterPill key="setup_signals" label={`Setup: ${text}`} onRemove={() => onRemoveFilter('setup_signals')} />
    );
  }

  // 12-week trend
  if ((filters.slope_12w_min ?? -50) > -50 || (filters.slope_12w_max ?? 50) < 50) {
    const lo = filters.slope_12w_min ?? -50;
    const hi = filters.slope_12w_max ?? 50;
    const sgn = (v: number) => `${v > 0 ? '+' : ''}${v}%`;
    pills.push(
      <FilterPill
        key="slope_12w"
        label={lo > -50 && hi < 50 ? `12-wk trend ${sgn(lo)} to ${sgn(hi)}` : lo > -50 ? `12-wk trend ≥ ${sgn(lo)}` : `12-wk trend ≤ ${sgn(hi)}`}
        onRemove={() => onRemoveFilter('slope_12w_min')}
      />
    );
  }

  // Open interest
  if ((filters.open_interest_min ?? 0) > 0 || (filters.open_interest_max ?? OI_SLIDER_MAX) < OI_SLIDER_MAX) {
    const k = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K` : `${v}`);
    const lo = filters.open_interest_min ?? 0;
    const hi = filters.open_interest_max ?? OI_SLIDER_MAX;
    pills.push(
      <FilterPill
        key="open_interest"
        label={lo > 0 && hi < OI_SLIDER_MAX ? `Open interest ${k(lo)}–${k(hi)}` : lo > 0 ? `Open interest ≥ ${k(lo)}` : `Open interest ≤ ${k(hi)}`}
        onRemove={() => onRemoveFilter('open_interest_min')}
      />
    );
  }

  // MACD
  if (filters.macd_signals && filters.macd_signals.length > 0) {
    pills.push(
      <FilterPill
        key="macd_signals"
        label={filters.macd_signals.length === 1 ? `MACD: ${filters.macd_signals[0] === 'Rising' ? 'rising' : 'crossed up'}` : 'MACD: 2 signals'}
        onRemove={() => onRemoveFilter('macd_signals')}
      />
    );
  }

  if (pills.length === 0) {
    return null;
  }

  return <>{pills}</>;
};

// Individual pill component
interface FilterPillProps {
  label: string;
  onRemove: () => void;
}

const FilterPill: React.FC<FilterPillProps> = ({ label, onRemove }) => {
  return (
    <div className="filter-pill">
      <span className="filter-pill-label">{label}</span>
      <button
        className="filter-pill-remove"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        aria-label={`Remove ${label} filter`}
      >
        ×
      </button>
    </div>
  );
};

export default ActiveFilterPills;
